import React, { useState, useEffect, useRef } from 'react';
import ReactSlider from 'react-slider';
import './Slider.css';
import { useFormatting, useTranslation } from '../../i18n/i18n';

const Slider = ({
	minValue,
	maxValue,
	selectedMinValue,
	selectedMaxValue,
	onSliderChange,
	unit,
	unitBeforeValue = false,
	step = 1,
	minDistance = 1,
}: {
	minValue: number;
	maxValue: number;
	selectedMinValue?: number;
	selectedMaxValue?: number;
	onSliderChange: (min: number, max: number) => void;
	unit: string;
	// currency symbols precede the amount in the euro markets, units always follow it
	unitBeforeValue?: boolean;
	step?: number;
	minDistance?: number;
}) => {
	const t = useTranslation().filtering;
	const { asLocaleNumber } = useFormatting();
	// local range state
	const [currentValues, setCurrentValues] = useState<[number, number]>([
		selectedMinValue ?? minValue,
		selectedMaxValue ?? maxValue,
	]);

	const [minValueInputIsFocused, setMinValueInputIsFocused] = useState(false);
	const [maxValueInputIsFocused, setMaxValueInputIsFocused] = useState(false);
	const [minInputValue, setMinInputValue] = useState<string>(currentValues[0].toString());
	const [maxInputValue, setMaxInputValue] = useState<string>(currentValues[1].toString());
	const minInputRef = useRef<HTMLInputElement>(null);
	const maxInputRef = useRef<HTMLInputElement>(null);
	const MAX_ALLOWED_VALUE = 1000000;
	const MIN_ALLOWED_VALUE = 0;

	useEffect(() => {
		setCurrentValues([selectedMinValue ?? minValue, selectedMaxValue ?? maxValue]);
	}, [minValue, maxValue, selectedMinValue, selectedMaxValue]);

	useEffect(() => {
		setMinInputValue(currentValues[0].toString());
		setMaxInputValue(currentValues[1].toString());
	}, [currentValues]);

	const handleSliderChange = (newValues: number[]) => {
		setCurrentValues([newValues[0], newValues[1]]);
	};

	const handleSliderAfterChange = (newValues: number[]) => {
		onSliderChange(newValues[0], newValues[1]);
	};

	const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputVal = Number(e.target.value);
		if (inputVal >= MIN_ALLOWED_VALUE && inputVal <= MAX_ALLOWED_VALUE) setMinInputValue(e.target.value);
	};

	const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputVal = Number(e.target.value);
		if (inputVal >= MIN_ALLOWED_VALUE && inputVal <= MAX_ALLOWED_VALUE) setMaxInputValue(e.target.value);
	};

	const handleMinInputBlur = () => {
		const parsed = Number(minInputValue);
		if (!isNaN(parsed)) {
			const newMinValue = Math.min(parsed, currentValues[1]);
			setCurrentValues([newMinValue, currentValues[1]]);
			onSliderChange(newMinValue, currentValues[1]);
			setMinInputValue(newMinValue.toString());
		} else {
			// reset to previous valid value if input is invalid
			setMinInputValue(currentValues[0].toString());
		}
	};

	const handleMaxInputBlur = () => {
		const parsed = Number(maxInputValue);
		if (!isNaN(parsed)) {
			const newMaxValue = Math.max(parsed, currentValues[0]);
			setCurrentValues([currentValues[0], newMaxValue]);
			onSliderChange(currentValues[0], newMaxValue);
			setMaxInputValue(newMaxValue.toString());
		} else {
			// reset to previous valid value if input is invalid
			setMaxInputValue(currentValues[1].toString());
		}
	};

	const handleMinValueTextClicked = () => {
		setMinValueInputIsFocused(true);
		setTimeout(() => {
			if (minInputRef.current) {
				minInputRef.current.focus();
			}
		}, 0);
	};

	const handleMaxValueTextClicked = () => {
		setMaxValueInputIsFocused(true);
		setTimeout(() => {
			if (maxInputRef.current) {
				maxInputRef.current.focus();
			}
		}, 0);
	};

	const handleMinInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleMinInputBlur();
			setMinValueInputIsFocused(false);
		}
	};

	const handleMaxInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleMaxInputBlur();
			setMaxValueInputIsFocused(false);
		}
	};

	return (
		<div className="slider-container">
			<div className="slider-values">
				<span
					className={`min-value-text ${minValueInputIsFocused ? 'hide' : ''}`}
					onClick={handleMinValueTextClicked}
				>
					{unitBeforeValue
						? `${unit} ${asLocaleNumber(currentValues[0])}`
						: `${asLocaleNumber(currentValues[0])} ${unit}`}
				</span>
				<input
					type="number"
					value={minInputValue}
					min={minValue}
					max={currentValues[1]}
					step={step}
					onChange={handleMinInputChange}
					className={`numeric-input-left ${minValueInputIsFocused ? '' : 'hide'}`}
					onBlur={() => {
						setMinValueInputIsFocused(false);
						handleMinInputBlur();
					}}
					onKeyDown={handleMinInputKeyDown}
					ref={minInputRef}
				/>

				<span className="range-to-text">{t.to}</span>

				<span
					className={`max-value-text ${maxValueInputIsFocused ? 'hide' : ''}`}
					onClick={handleMaxValueTextClicked}
				>
					{unitBeforeValue
						? `${unit} ${asLocaleNumber(currentValues[1])}`
						: `${asLocaleNumber(currentValues[1])} ${unit}`}
				</span>
				<input
					type="number"
					value={maxInputValue}
					min={currentValues[0]}
					max={maxValue}
					step={step}
					onChange={handleMaxInputChange}
					className={`numeric-input-right ${maxValueInputIsFocused ? '' : 'hide'}`}
					onBlur={() => {
						setMaxValueInputIsFocused(false);
						handleMaxInputBlur();
					}}
					onKeyDown={handleMaxInputKeyDown}
					ref={maxInputRef}
				/>
			</div>

			<ReactSlider
				className="horizontal-slider"
				thumbClassName="sliderthumb"
				trackClassName="track"
				min={minValue}
				max={maxValue}
				step={step}
				value={currentValues}
				onChange={handleSliderChange}
				onAfterChange={handleSliderAfterChange}
				pearling
				minDistance={minDistance}
			/>
		</div>
	);
};

export default Slider;
